<template>
  <div>
    <navigation-main bg="true" />
    <div class="main-content">
      <main role="main" class="flex-grow-1 overflow-auto">
        <div id="content">
          <section class="page-section">
            <div class="container">
              <div class="row ">
                <div class="col-12">
                  <h1 class="text-black mb-0">
                    Benchmarks
                  </h1>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    We provide a benchmark suite together with an evaluation
                    server for ACDC. For proper benchmarking, the benchmarks use
                    the test set, for which the ground truth is withheld.
                    Authors can upload the results of their method on the test
                    set and get its respective quantitative performance. We
                    offer two benchmarks, on
                    <a href="#semanticSegmentation">semantic segmentation</a>,
                    <a href="#uncertaintyAwareSemanticSegmentation">uncertainty-aware semantic segmentation</a>,
                    <a href="#objectDetection">object detection</a>, and
                    <a href="#panopticSegmentation">panoptic segmentation</a>.
                  </p>
                  <p>
                    Authors can submit results either for the complete test set
                    comprising all four adverse conditions, i.e., fog,
                    nighttime, rain, and snow, or for any individual condition.
                    Accordingly, there are five separate leaderboards for each
                    benchmark.
                  </p>
                  <p>
                    In order to submit your results, you first need to register.
                    After registration, please follow the instructions on the
                    <a href="../submit">submission page</a>.
                  </p>
                </div>
              </div>

              <div class="m-5"></div>

              <div class="row " id="semseg">
                <!-- Work around to account for navigation height-->
                <div class="scroll-to-element" id="semanticSegmentation"></div>
                <div class="col-12">
                  <h2 class="text-black mb-0">
                    Semantic Segmentation
                  </h2>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    The first ACDC task is semantic segmentation. The required
                    output is a per-pixel assignment to a label from the set of
                    19 semantic classes included in ACDC.
                  </p>
                  <p>
                    Evaluation is performed using the standard
                    intersection-over-union (IoU) metric, also known as Jaccard
                    index, at the level of classes, and averaging over all
                    classes.
                  </p>
                </div>
              </div>
              <div class="row">
                <ResultTables
                  :tasks="tasksSemseg"
                  :isSemantic="true"
                  :additional-headers="additionalHeaders"
                  :has-code="true"
                ></ResultTables>
              </div>
              <div class="m-5"></div>

              <div class="row ">
                <div class="col-12">
                  <!-- Work around to account for navigation height-->
                  <div
                    class="scroll-to-element"
                    id="uncertaintyAwareSemanticSegmentation"
                  ></div>
                  <h2 class="text-black mb-0">
                    Uncertainty-Aware Semantic Segmentation
                  </h2>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    The second ACDC task is uncertainty-aware semantic
                    segmentation. The required outputs for each image are two:
                  </p>
                  <ol class="ml-3">
                    <li>
                      A semantic prediction, similarly to standard semantic
                      segmentation.
                    </li>
                    <li>
                      A confidence map associated with the semantic prediction,
                      with values ranging from 0 to 1.
                    </li>
                  </ol>
                  <p>
                    Evaluation is performed based on the semantic ground-truth
                    annotations and the binary invalid masks available in ACDC
                    using two metrics:
                  </p>
                  <ol class="ml-3">
                    <li>
                      The average uncertainty-aware intersection-over-union (Avg
                      UIoU). Calculation of this metric involves thresholding
                      the confidence maps at different values, computing the
                      uncertainty-aware intersection-over-union (UIoU) for each
                      threshold, and averaging over all thresholds. UIoU is
                      defined as UIoU = (TP+TI) / (TP+TI+FP+FN+FI), where TP,
                      TI, FP, FN, and FI are the numbers of true positive, true
                      invalid, false positive, false negative, and false invalid
                      pixels, respectively [<a href="#mgcdauiou">1</a>].
                    </li>
                    <li>
                      The standard IoU metric.
                    </li>
                  </ol>
                </div>
              </div>
              <div class="row">
                <ResultTables
                  :tasks="tasksSemsegWithConfidence"
                  :isUncertain="true"
                  :additional-headers="additionalHeaders"
                  :has-code="true"
                ></ResultTables>
              </div>
              <div class="m-5"></div>

              <div class="row ">
                <div class="col-12">
                  <!-- Work around to account for navigation height-->
                  <div
                    class="scroll-to-element"
                    id="objectDetection"
                  ></div>
                  <h2 class="text-black mb-0">
                    Object Detection
                  </h2>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    The third ACDC task is object detection.
                    The required output for each image is a set of bounding
                    boxes, each of which is assigned a label from the set of
                    "thing" classes as well as a confidence score between 0 and 1. 
                  </p>
                  <p>
                    Evaluation is performed using the standard average precision (AP)
                    metric at the level of classes and averaging over all "things" classes.
                  </p>
                </div>
              </div>
              <div class="row">
                <ResultTables
                  :tasks="tasksObjDet"
                  :isDetection="true"
                  :additional-headers="additionalHeaders"
                  :has-code="true"
                ></ResultTables>
              </div>
              <div class="m-5"></div>

              <div class="row ">
                <div class="col-12">
                  <!-- Work around to account for navigation height-->
                  <div
                    class="scroll-to-element"
                    id="panopticSegmentation"
                  ></div>
                  <h2 class="text-black mb-0">
                    Panoptic Segmentation
                  </h2>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    The fourth ACDC task is panoptic segmentation.
                    The required output
                    is a per-pixel assignment to a label from the set of
                    19 semantic classes included in ACDC, with an additional
                    assignment of pixels that are predicted to belong to a "thing"
                    class to distinct instances of this class in the image. 
                  <p>
                    Evaluation is performed using the standard panoptic quality (PQ)
                    metric at the level of classes and averaging over all classes.
                    Average results over "things" classes (PQ things) and "stuff" classes
                    (PQ stuff) are also reported.
                  </p>
                </div>
              </div>
              <div class="row">
                <ResultTables
                  :tasks="tasksPanSeg"
                  :isPanoptic="true"
                  :additional-headers="additionalHeaders"
                  :has-code="true"
                ></ResultTables>
              </div>
            </div>
          </section>
          <section class="page-section">
            <div class="container">
              <div class="row ">
                <div class="col-12">
                  <h4 class="text-black mb-0">
                    References
                  </h4>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p id="mgcdauiou">
                    [1] Christos Sakaridis, Dengxin Dai, and Luc Van Gool,
                    "Map-Guided Curriculum Domain Adaptation and
                    Uncertainty-Aware Evaluation for Semantic Nighttime Image
                    Segmentation," IEEE T-PAMI, 2022.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import NavigationMain from "@/views/navigation/navigation-main";
import { mapActions } from "vuex";
import ResultTables from "@/views/benchmark/ResultTables";
import moment from "moment";
import { groupBy } from "@/helpers/utils";

export default {
  name: "SemSegPage.vue",
  components: {
    ResultTables,
    NavigationMain
  },

  methods: {
    submissionMapper(submission) {
      // eslint-disable-next-line
      submission.created_at = moment(submission.created_at).format(
        "MM/DD/YYYY HH:mm"
      );
      return submission;
    },
    groupBy,
    ...mapActions("api", ["getBenchmark"]),
    loadBenchmarks() {
      this.getBenchmark("semSeg").then(data => {
        this.items = data.submissions.map(this.submissionMapper);

        const semseg = data.submissions.filter(e => e.challenge === "semseg");

        const semsegUncertain = data.submissions.filter(
          e => e.challenge === "semSegUncertain"
        );
        
        const objdet = data.submissions.filter(e => e.challenge === "objDet");
        const panseg = data.submissions.filter(e => e.challenge === "panSeg");

        this.tasksSemseg = this.groupBy(semseg, "task");
        this.tasksSemsegWithConfidence = this.groupBy(semsegUncertain, "task");
        this.tasksObjDet = this.groupBy(objdet, "task");
        this.tasksPanSeg = this.groupBy(panseg, "task");
      });
    }
  },
  created() {
    this.loadBenchmarks();
  },
  data() {
    return {
      tasksSemseg: { all: [1, 2, 3, 4, 5] },
      tasksSemsegWithConfidence: { all: [1, 2, 3, 4, 5] },
      tasksObjDet: { all: [1, 2, 3, 4, 5] },
      tasksPanSeg: { all: [1, 2, 3, 4, 5] },
      items: [],
      additionalHeaders: [
        {
          text: "Code",
          align: "end",
          filterable: true,
          value: "code"
        }
      ]
    };
  }
};
</script>

<style scoped lang="scss">
.input-form {
  .row {
    padding-top: 2px;
    padding-bottom: 2px;
  }
}

.scroll-to-element {
  position: absolute;
  margin-top: -80px;
}
</style>
